import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Col, Row, Form } from 'antd';
import withAuthorizationData from 'HOC/Authorization';
import AuthLeft from 'HOC/AuthLayout/AuthLeft';
import AuthRight from 'HOC/AuthLayout/AuthRight';
import Logo from 'components/Common/Logo';
import AuthImg from 'components/Auth/AuthImg';
import AuthTitleText from 'components/Auth/AuthTitleText';
import AuthSubButton from 'components/Auth/AuthSubButton';
import FormInput from 'components/Common/Input/FormInput';
import StyledButton from 'components/Common/Buttons/StyledButton';

import { ROUTING, ROUTING as routing } from 'config';
import { AUTH_TYPES, FORM_INPUT_TYPES, LOGO } from 'utils/constants';

import styles from './styles.module.less';
import useBrandTitle from 'utils/hooks/useBrandTitle';
import { useSignInBackground } from 'utils/hooks/useSignInBackground';
import { useBrandSignUpLink } from 'utils/hooks/useBrandSignUpLink';
import { useBrand } from 'modules/shared/brand/hooks/useBrand';
import { Brand } from 'constants/brand';

const { SIGN_IN } = AUTH_TYPES;

const SignIn = ({
  form,
  onFinish,
  isLoading,
  isTwoFactorAuth,
  onLogin,
  onCancelTwoFactorAuth,
  onTwoFactorAuth,
}) => {
  const brandTitle = useBrandTitle();
  const brandedBackground = useSignInBackground();
  const signUpLink = useBrandSignUpLink();
  const { brand } = useBrand();

  const isFm = brand === Brand.FM;

  return (
    <>
      <AuthLeft>
        <Logo text={LOGO.FULL} isColumn />
        <AuthImg image={brandedBackground} />
        <AuthTitleText
          title={SIGN_IN.LEFT_TITLE}
          text={SIGN_IN.LEFT_SUB_TITLE.replace(/%BRAND%/gi, brandTitle)}
        />
      </AuthLeft>
      <AuthRight>
        <Row className={styles.signInAuthLogo}>
          <Col xs={24} md={0}>
            <Logo text={LOGO.SHORT} />
          </Col>
        </Row>
        <AuthTitleText
          classNameText={styles.signInTextMargin}
          title={SIGN_IN.TITLE}
          text={SIGN_IN.SUB_TITLE.replace(/%BRAND%/gi, brandTitle)}
        />
        <Form
          className={styles.signInForm}
          form={form}
          onFinish={onFinish(isTwoFactorAuth ? onTwoFactorAuth : onLogin)}
          name={SIGN_IN.FORM_NAME}
        >
          {isTwoFactorAuth ? (
            <>
              <FormInput
                type={SIGN_IN.TFA_CODE}
                placeholder={SIGN_IN.TFA_CODE}
                label={SIGN_IN.TFA_CODE_TITLE}
                className={styles.inputIndent}
                extraLabel={
                  <span
                    className={styles.signInLink}
                    onClick={onCancelTwoFactorAuth}
                  >
                    Cancel
                  </span>
                }
              />
            </>
          ) : (
            <>
              <FormInput
                type={FORM_INPUT_TYPES.EMAIL}
                placeholder={SIGN_IN.EMAIL_PLACEHOLDER}
                label={SIGN_IN.EMAIL}
                className={styles.inputIndent}
              />
              <FormInput
                type={FORM_INPUT_TYPES.PASSWORD}
                placeholder={SIGN_IN.PASSWORD_PLACEHOLDER}
                label={SIGN_IN.PASSWORD}
                extraLabel={
                  <Link className={styles.signInLink} to={routing.RESET}>
                    Forgot Password?
                  </Link>
                }
              />
            </>
          )}
          <StyledButton
            type="primary"
            htmlType="submit"
            className={styles.signInGradient}
            text={isTwoFactorAuth ? SIGN_IN.TFA_CODE_SEND : SIGN_IN.GRADIENT}
            loading={isLoading}
            disabled={isLoading}
          />
        </Form>
        <AuthSubButton
          text={SIGN_IN.GRADIENT_SUB_TEXT}
          link={isFm ? ROUTING.SIGN_UP : signUpLink}
          linkText={SIGN_IN.GRADIENT_SUB_LINK_TEXT}
          isExternalLink={!isFm}
        />
      </AuthRight>
    </>
  );
};

SignIn.propTypes = {
  form: PropTypes.object,
  isTwoFactorAuth: PropTypes.bool,
  isLoading: PropTypes.bool,
  onLogin: PropTypes.func,
  onFinish: PropTypes.func,
  onCancelTwoFactorAuth: PropTypes.func.isRequired,
  onTwoFactorAuth: PropTypes.func.isRequired,
};

export default () => withAuthorizationData(SignIn);
